import React, { useMemo, useState } from 'react';
import {
  Modal,
  Table,
  Button,
  Tag,
  Typography,
  Input,
  message,
  Popconfirm,
  Tooltip,
} from 'antd';
import { EyeOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCardDetail } from '../../context/CardDetailContext';
import dayjs from 'dayjs';
import maturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';
import { getSearchCheckboxFilterProps } from '../../utils/table-filter-helper';

const { Text } = Typography;

function formatCardNumber(cardNumber?: string) {
  if (!cardNumber || cardNumber.length < 4) return cardNumber || '';
  return `***${cardNumber.slice(-4)}`;
}

interface WrongPaymentMaturityScheduleModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  schedules: any[];
  role?: string | null; // thêm prop role
}

const WrongPaymentMaturityScheduleModal: React.FC<
  WrongPaymentMaturityScheduleModalProps
> = ({ open, onClose, loading, schedules, role }) => {
  const navigate = useNavigate();
  const { setSelectedCard, setMaturityScheduleId, setMaturityYear } =
    useCardDetail();
  const [localData, setLocalData] = useState<any[]>(schedules);
  React.useEffect(() => {
    setLocalData(schedules);
  }, [schedules]);

  const handleCheckPayment = async (record: any) => {
    try {
      await maturityScheduleService.checkPaymentMaturitySchedule(record.id);
      setLocalData((prev) => prev.filter((item) => item.id !== record.id));
      message.success('Đã đánh dấu kiểm tra thanh toán!');
    } catch (e) {
      message.error('Lỗi khi cập nhật trạng thái kiểm tra thanh toán!');
    }
  };

  // Filter options
  const cardNumbers = useMemo(
    () =>
      Array.from(
        new Set(schedules.map((s) => s.creditCard?.cardNumber).filter(Boolean))
      ).map((n) => ({ text: formatCardNumber(n), value: n })),
    [schedules]
  );
  const cardHolderNames = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.creditCard?.cardHolderName).filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [schedules]
  );
  const customerNames = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.creditCard?.customer?.name).filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [schedules]
  );
  const employeeOptions = useMemo(() => {
    const allEmps: any[] = [];
    schedules.forEach((s: any) => {
      if (Array.isArray(s.employees)) {
        s.employees.forEach((e: any) => {
          const name = e.fullName || e.name;
          if (name) {
            allEmps.push({
              value: name,
              label: `${name}${e.employeeCode ? ` (${e.employeeCode})` : ''}`,
            });
          }
        });
      }
    });

    const uniqueMap = new Map();
    allEmps.forEach((e: any) => {
      if (!uniqueMap.has(e.value)) {
        uniqueMap.set(e.value, e);
      }
    });
    return Array.from(uniqueMap.values());
  }, [schedules]);

  const columns = [
    {
      title: 'Ngày đến hạn',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (d: string) => (
        <Tag color="red">{dayjs(d).format('DD-MM-YYYY')}</Tag>
      ),
      sorter: (a: any, b: any) =>
        dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm ngày (dd-mm-yyyy)"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 180, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        dayjs(record.dueDate).format('DD-MM-YYYY').includes(value),
    },
    {
      title: 'Số thẻ',
      dataIndex: ['creditCard', 'cardNumber'],
      key: 'cardNumber',
      filters: cardNumbers,
      onFilter: (value: any, record: any) =>
        record.creditCard?.cardNumber === value,
      render: (_: any, r: any) => formatCardNumber(r.creditCard?.cardNumber),
    },
    {
      title: 'Chủ thẻ',
      dataIndex: ['creditCard', 'cardHolderName'],
      key: 'cardHolderName',
      filters: cardHolderNames,
      filterSearch: true,
      onFilter: (value: any, record: any) =>
        record.creditCard?.cardHolderName === value,
      render: (_: any, r: any) => r.creditCard?.cardHolderName,
    },
    {
      title: 'Khách hàng',
      dataIndex: ['creditCard', 'customer', 'name'],
      key: 'customer',
      filters: customerNames,
      filterSearch: true,
      onFilter: (value: any, record: any) =>
        record.creditCard?.customer?.name === value,
      render: (_: any, r: any) => r.creditCard?.customer?.name,
    },
    {
      title: 'Số rút',
      dataIndex: 'totalWithdrawalAmount',
      key: 'totalWithdrawalAmount',
      sorter: (a: any, b: any) =>
        (a.totalWithdrawalAmount || 0) - (b.totalWithdrawalAmount || 0),
      render: (v: number) => v?.toLocaleString(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số rút"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.totalWithdrawalAmount || '').toString().includes(value),
    },
    {
      title: 'Số nạp',
      dataIndex: 'totalDepositAmount',
      key: 'totalDepositAmount',
      sorter: (a: any, b: any) =>
        (a.totalDepositAmount || 0) - (b.totalDepositAmount || 0),
      render: (v: number) => v?.toLocaleString(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số nạp"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.totalDepositAmount || '').toString().includes(value),
    },
    {
      title: 'Phí thu (%)',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      sorter: (a: any, b: any) => (a.feeApplied || 0) - (b.feeApplied || 0),
      render: (v: number) => v?.toFixed(2),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm phí thu (%)"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 120, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.feeApplied || '').toString().includes(value),
    },
    {
      title: 'Chênh lệch',
      dataIndex: 'balanceDifference',
      key: 'balanceDifference',
      sorter: (a: any, b: any) =>
        Math.abs(a.balanceDifference || 0) - Math.abs(b.balanceDifference || 0),
      render: (v: number) => v?.toLocaleString(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm chênh lệch"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.balanceDifference || '').toString().includes(value),
    },
    {
      title: 'Thành tiền chuẩn',
      key: 'thanhTien',
      render: (_: any, r: any) => {
        const totalWithdrawal = r.totalWithdrawalAmount || 0;
        const totalDeposit = r.totalDepositAmount || 0;
        const fee = r.feeApplied || 0;
        const chenhLech = totalDeposit - totalWithdrawal;
        const thanhTien = totalWithdrawal * (fee / 100) + chenhLech;
        return thanhTien.toLocaleString();
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm thành tiền"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) => {
        const totalWithdrawal = record.totalWithdrawalAmount || 0;
        const totalDeposit = record.totalDepositAmount || 0;
        const fee = record.feeApplied || 0;
        const chenhLech = totalDeposit - totalWithdrawal;
        const thanhTien = totalWithdrawal * (fee / 100) + chenhLech;
        return thanhTien.toString().includes(value);
      },
    },
    {
      title: 'Tổng thanh toán',
      dataIndex: 'totalPaymentAmount',
      key: 'totalPaymentAmount',
      sorter: (a: any, b: any) =>
        (a.totalPaymentAmount || 0) - (b.totalPaymentAmount || 0),
      render: (v: number) => v?.toLocaleString(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm thanh toán"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.totalPaymentAmount || '').toString().includes(value),
    },
    {
      title: 'Phụ trách',
      dataIndex: 'employees',
      key: 'employees',
      ...getSearchCheckboxFilterProps('Phụ trách', employeeOptions),
      onFilter: (value: any, record: any) => {
        if (!Array.isArray(record.employees)) return false;
        return record.employees.some(
          (e: any) => (e.fullName || e.name) === value
        );
      },
      render: (emps: any[]) =>
        Array.isArray(emps)
          ? emps.map((e, idx) => (
              <Tag color="blue" key={e.id || idx}>
                {e.fullName || e.name || e.employeeCode || e.id}
                {e.employeeCode ? ` (${e.employeeCode})` : ''}
              </Tag>
            ))
          : '',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm ghi chú"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 180, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.notes || '')
          .toLowerCase()
          .includes((value || '').toLowerCase()),
      render: (n: string) => (n ? <Text type="secondary">{n}</Text> : null),
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right' as const,
      width: 150,
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Xem chi tiết">
            <Button
              icon={<EyeOutlined />}
              type="link"
              size="small"
              onClick={() => {
                setSelectedCard(record.creditCard);
                setMaturityScheduleId(record.id);
                setMaturityYear(dayjs(record.dueDate).year());
                navigate('/card-detail');
              }}
            />
          </Tooltip>
          {/* Ẩn nút check nếu là employee */}
          {role !== 'ROLE_EMPLOYEE' && (
            <Popconfirm
              title="Đánh dấu đã kiểm tra thanh toán?"
              onConfirm={() => handleCheckPayment(record)}
              okText="Đã kiểm tra"
              cancelText="Hủy"
            >
              <Tooltip title="Đã kiểm tra">
                <Button
                  icon={<CheckOutlined />}
                  type="primary"
                  ghost
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span style={{ fontWeight: 600, fontSize: 18 }}>
          Kỳ đáo hạn thanh toán sai
        </span>
      }
      footer={null}
      width={1200}
      bodyStyle={{ padding: 24 }}
    >
      <div
        ref={modalBodyRef}
        style={{ maxHeight: 600, overflowY: 'auto', position: 'relative' }}
      >
        <Table
          columns={columns}
          dataSource={localData.filter((r) => !r.isCheckedPayment)}
          loading={loading}
          rowKey={(r) => r.id}
          scroll={{ x: 1400 }}
          sticky={{ offsetHeader: 0, getContainer: () => modalBodyRef.current! }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          bordered
          size="middle"
          locale={{ emptyText: 'Không có kỳ đáo hạn nào.' }}
        />
      </div>
    </Modal>
  );
};

export default WrongPaymentMaturityScheduleModal;
